import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Building, Fuel, TrendingUp, MapPin, Calendar, Users, DollarSign } from 'lucide-react';

export const Projects = () => {
  const majorProjects = [
    {
      id: 1,
      category: 'Infrastructure Énergétique',
      icon: Fuel,
      title: 'Capacités de Stockage Pétrolier Stratégique',
      value: '120M USD',
      timeline: '2024-2029',
      status: 'Phase de préparation',
      color: '#FF6600',
      description: 'Kip Energy, en partenariat avec Wayne Africa, développe 140 000 m³ de nouvelles capacités de stockage pétrolier sur trois sites stratégiques.',
      impacts: [
        'Sécurité énergétique nationale renforcée',
        'Autonomie de 90 jours de consommation nationale',
        '200+ emplois directs créés',
        'Standards internationaux API/NFPA'
      ],
      locations: [
        { name: 'Abidjan', capacity: '60 000 m³' },
        { name: 'Yamoussoukro', capacity: '40 000 m³' },
        { name: 'San-Pédro', capacity: '40 000 m³' }
      ]
    },
    {
      id: 2,
      category: 'Développement Immobilier',
      icon: Building,
      title: '179+ Hectares en Développement',
      value: '179+ Ha',
      timeline: '2025-2035',
      status: 'Portfolio actif',
      color: '#00A651',
      description: 'KIDH développe un vaste portfolio immobilier à travers la Côte d\'Ivoire, allant des cités résidentielles aux complexes touristiques.',
      projects: [
        {
          name: 'Bingerville - Cité Résidentielle',
          size: '60 ha',
          details: '3 000+ logements, centres commerciaux, infrastructures',
          timeline: '2025-2035'
        },
        {
          name: 'Angré - Complexe Commercial',
          size: '10 ha',
          details: 'Immeubles de bureaux classe A, espaces premium',
          timeline: '2026-2030'
        },
        {
          name: 'Songon - Résidences Lacustres',
          size: '35 ha',
          details: 'Villas haut de gamme, marina privée, club house',
          timeline: '2027-2033'
        },
        {
          name: 'Sassandra - Complexe Touristique',
          size: '50 ha',
          details: 'Golf 18 trous, hôtel 4 étoiles, résidences balnéaires',
          timeline: '2028-2035'
        },
        {
          name: 'Yamoussoukro - Campus Universitaire',
          size: '24 ha',
          details: 'Résidences étudiantes (2 000 lits), infrastructures',
          timeline: '2026-2029'
        }
      ]
    },
    {
      id: 3,
      category: 'Expansion Réseau',
      icon: TrendingUp,
      title: 'Déploiement National de Stations-Services Wayne',
      value: '50+ Stations',
      timeline: '2024-2030',
      status: 'En cours',
      color: '#FF6600',
      description: 'Développer un réseau de 50+ stations-services Wayne à travers la Côte d\'Ivoire d\'ici 2030.',
      features: [
        'Stations modernes avec boutiques EKIPTECH intégrées',
        'Services premium et programme de fidélité KIPAY',
        'Modèle partenaire avec support EKIP'
      ],
      distribution: [
        { zone: 'Abidjan et périphérie', count: '15 stations' },
        { zone: 'Axes autoroutiers', count: '20 stations' },
        { zone: 'Villes secondaires', count: '15 stations' }
      ]
    }
  ];

  const futureProjects = [
    {
      title: 'Pipeline Yamoussoukro-Abidjan',
      category: 'Infrastructure Énergétique',
      description: 'Étude de faisabilité pour un pipeline reliant Yamoussoukro au port d\'Abidjan',
      status: 'Étude'
    },
    {
      title: 'Expansion Régionale',
      category: 'Évaluation',
      description: 'EKIP Burkina Faso & Mali - Évaluation d\'opportunités d\'expansion',
      status: 'Évaluation'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Nos Projets
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Des investissements stratégiques pour transformer la Côte d'Ivoire
          </p>
        </div>
      </section>

      {/* Major Projects */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Projets Phares en Cours
            </h2>
          </div>

          <div className="space-y-16">
            {majorProjects.map((project) => {
              const Icon = project.icon;
              return (
                <Card key={project.id} className="border-2 shadow-xl overflow-hidden" style={{ borderColor: project.color }}>
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-8 pb-6" style={{ background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)` }}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="p-4 rounded-lg" style={{ backgroundColor: project.color }}>
                            <Icon className="text-white" size={32} />
                          </div>
                          <div>
                            <Badge className="mb-2" style={{ backgroundColor: project.color }}>
                              {project.category}
                            </Badge>
                            <h3 className="text-3xl font-bold text-[#003366]">{project.title}</h3>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-4xl font-bold" style={{ color: project.color }}>
                            {project.value}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6 mt-6">
                        <div className="flex items-center space-x-2">
                          <Calendar size={18} className="text-gray-600" />
                          <span className="text-gray-700 font-medium">{project.timeline}</span>
                        </div>
                        <Badge variant="outline" className="border-2" style={{ borderColor: project.color, color: project.color }}>
                          {project.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <p className="text-lg text-gray-700 leading-relaxed mb-8">
                        {project.description}
                      </p>

                      {/* Impacts */}
                      {project.impacts && (
                        <div className="mb-8">
                          <h4 className="text-xl font-bold text-[#003366] mb-4 flex items-center">
                            <TrendingUp className="mr-2" style={{ color: project.color }} size={24} />
                            Impact
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {project.impacts.map((impact, i) => (
                              <div key={i} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: project.color }} />
                                <span className="text-gray-700">{impact}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Locations */}
                      {project.locations && (
                        <div className="mb-8">
                          <h4 className="text-xl font-bold text-[#003366] mb-4 flex items-center">
                            <MapPin className="mr-2" style={{ color: project.color }} size={24} />
                            Sites Stratégiques
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {project.locations.map((location, i) => (
                              <div key={i} className="border-2 p-4 rounded-lg" style={{ borderColor: project.color }}>
                                <div className="font-bold text-[#003366] mb-1">{location.name}</div>
                                <div className="text-lg font-semibold" style={{ color: project.color }}>
                                  {location.capacity}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Projects list */}
                      {project.projects && (
                        <div className="space-y-4">
                          {project.projects.map((subProject, i) => (
                            <div key={i} className="border-l-4 pl-6 py-3" style={{ borderColor: project.color }}>
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="text-lg font-bold text-[#003366]">{subProject.name}</h4>
                                <Badge style={{ backgroundColor: `${project.color}20`, color: project.color }}>
                                  {subProject.size}
                                </Badge>
                              </div>
                              <p className="text-gray-600 mb-2">{subProject.details}</p>
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <Calendar size={14} />
                                <span>{subProject.timeline}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Features */}
                      {project.features && (
                        <div className="mb-8">
                          <ul className="space-y-3">
                            {project.features.map((feature, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${project.color}20` }}>
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color }} />
                                </div>
                                <span className="text-gray-700">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Distribution */}
                      {project.distribution && (
                        <div>
                          <h4 className="text-xl font-bold text-[#003366] mb-4">Répartition Géographique</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {project.distribution.map((dist, i) => (
                              <div key={i} className="bg-gradient-to-br from-gray-50 to-white p-5 rounded-lg border border-gray-200">
                                <div className="text-2xl font-bold mb-1" style={{ color: project.color }}>
                                  {dist.count}
                                </div>
                                <div className="text-gray-600">{dist.zone}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Future Projects */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              Projets en Développement
            </h2>
            <p className="text-xl text-gray-600">
              Les initiatives de demain
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {futureProjects.map((project, index) => (
              <Card key={index} className="border-2 border-[#FFB81C] hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <Badge className="bg-[#FFB81C] text-white">{project.category}</Badge>
                    <Badge variant="outline" className="border-[#FFB81C] text-[#FFB81C]">{project.status}</Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-[#003366] mb-4">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFB81C] mb-2">120M</div>
              <div className="text-white text-lg">USD Investis</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFB81C] mb-2">179+</div>
              <div className="text-white text-lg">Hectares</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFB81C] mb-2">50+</div>
              <div className="text-white text-lg">Stations Wayne</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFB81C] mb-2">5</div>
              <div className="text-white text-lg">Sites Majeurs</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};